import { TextWithUnsureName } from "@/components/molecules/unsure-name-mark";
import type {
  CalendarEvent,
  Schedule,
  TimeOff,
} from "@/features/calendar/types";
import type { Reminder } from "@/features/reminders/types";
import { getColorForString } from "@/features/utils/colors";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  ClockCounterClockwiseIcon,
  VideoCameraIcon,
} from "@phosphor-icons/react";
import { isSameDay } from "date-fns";
import { useEffect, useState } from "react";
import { useCalendarView } from "../hooks/use-calendar-view";
import { ReminderChip, reminderTopOffset } from "./reminder-chip";
import { calculateEventLayout } from "../hooks/use-events-layouts";
import { useScheduleAvailability } from "../hooks/use-schedule-availability";

export const DayColumn = ({
  day: currentDate,
  dayIndex,
  events,
  reminders = [],
  schedules,
  timeOffs,
  selectedMembers,
  onEventClick,
  onSlotClick,
  className,
  draggedEvent,
  dragOverSlot,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  day: Date;
  dayIndex: number;
  events: CalendarEvent[];
  reminders?: Reminder[];
  schedules?: Schedule[];
  timeOffs?: TimeOff[];
  selectedMembers: string[];
  onEventClick: (event: CalendarEvent) => void;
  onSlotClick: (startTime: number, endTime: number) => void;
  className?: string;
  draggedEvent: CalendarEvent | null;
  dragOverSlot: { hour: number; minute: number; dayIndex: number } | null;
  onDragStart: (event: CalendarEvent) => void;
  onDragOver: (dayIndex: number, hour: number, minute: number) => void;
  onDrop: (day: Date, hour: number, minute: number) => void;
  onDragEnd: () => void;
}) => {
  const { t } = useTranslation("calendar");
  const { format } = useDateFormat();
  const { timeSlots, getEventStyle, createSlotClickHandler } =
    useCalendarView();
  const { getSlotAvailability } = useScheduleAvailability(
    schedules || [],
    timeOffs || [],
    selectedMembers,
  );
  const isToday = currentDate ? isSameDay(currentDate, new Date()) : false;

  const eventsLayout = calculateEventLayout(events);

  // Helper to check if event is draggable
  const isEventDraggable = (event: CalendarEvent) => {
    return event.is_primary && !event.sync_status.includes("pending");
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, event: CalendarEvent) => {
    if (!isEventDraggable(event)) {
      e.preventDefault();
      return;
    }
    onDragStart(event);
    e.dataTransfer.effectAllowed = "move";
    // Set a transparent drag image
    const img = new Image();
    img.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    e.dataTransfer.setDragImage(img, 0, 0);
  };

  // Handle drag over slots
  const handleDragOver = (e: React.DragEvent, hour: number, minute: number) => {
    if (!draggedEvent) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    onDragOver(dayIndex, hour, minute);
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent, hour: number, minute: number) => {
    e.preventDefault();
    if (!draggedEvent) return;
    onDrop(currentDate, hour, minute);
  };

  return (
    <div className={cn("relative", className)}>
      {isToday && <CurrentTime />}

      {timeSlots.map(({ hour, minute }) => {
        const slotKey = `${hour}-${minute}`;
        const availability = getSlotAvailability(currentDate, hour, minute);
        const isEveryoneOff = availability.affectedMembers.includes("*");
        const hasMemberTimeOff =
          availability.affectedMembers.length > 0 && !isEveryoneOff;

        return (
          <div
            key={slotKey}
            className={cn(
              "h-3 relative",
              (availability.isClosed || isEveryoneOff) &&
                "bg-black/5 dark:bg-black/25 border-none",
              !availability.isClosed &&
                !isEveryoneOff &&
                "hover:bg-accent/50 hover:cursor-pointer",
              draggedEvent &&
                dragOverSlot?.dayIndex === dayIndex &&
                dragOverSlot?.hour === hour &&
                dragOverSlot?.minute === minute &&
                "bg-primary/20",
            )}
            onClick={() =>
              !availability.isClosed &&
              !isEveryoneOff &&
              createSlotClickHandler(onSlotClick, currentDate)(hour, minute)
            }
            onDragOver={(e) => handleDragOver(e, hour, minute)}
            onDrop={(e) => handleDrop(e, hour, minute)}
          >
            {(minute === 0 || minute === 45) &&
              ["top", "bottom"].map((pos) => (
                <div
                  key={pos}
                  className={cn(
                    "border-b border-b-slate-500/0 absolute left-0 right-0",
                    pos === "top" && "-top-px",
                    pos === "bottom" && "bottom-0",
                    minute === 0 && pos === "top" && "border-b-slate-500/10",
                    minute === 45 &&
                      pos === "bottom" &&
                      "border-b-slate-500/10",
                    (availability.isClosed || isEveryoneOff) && "0",
                  )}
                />
              ))}
            {hasMemberTimeOff && (
              <div className="absolute left-0 top-0 bottom-0 flex">
                {availability.affectedMembers.map(
                  (memberId: string, idx: number) => (
                    <div
                      key={memberId}
                      className="w-1 bg-muted-foreground/30"
                      style={{ marginLeft: idx > 0 ? "2px" : "0" }}
                    />
                  ),
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Planned follow-ups: their own lane, so they never hide an appointment. */}
      {reminders.map((reminder) => (
        <ReminderChip
          key={reminder.id}
          reminder={reminder}
          top={reminderTopOffset(reminder.scheduled_at)}
        />
      ))}

      {events.map((event) => {
        const style = getEventStyle(event);
        if (!style) return null;

        // Check if any event attendees have time-off at the event start time
        const eventStart = new Date(event.start_time);
        // Use local time for consistency with calendar display
        const availability = getSlotAvailability(
          currentDate,
          eventStart.getHours(),
          eventStart.getMinutes(),
        );
        const affectedAttendees = event.attendees?.attendees?.filter((a) =>
          availability.affectedMembers.includes(a.email),
        );

        const isDraggable = isEventDraggable(event);
        const isBeingDragged = draggedEvent?.id === event.id;
        // A booking the customer has not confirmed yet is a slot that may still
        // free up: the team has to be able to tell it apart from a firm one at a
        // glance, not by opening it.
        const isUnconfirmed = event.status === "tentative";

        const color = event.service_id
          ? getColorForString(event.service_id)
          : "var(--muted-foreground)";
        const timeRange = `${format(event.start_time, "p")} – ${format(event.end_time, "p")}`;
        // What a block has room for, at 3rem an hour: one line under 45
        // minutes, two under an hour, then the title on up to three lines and
        // its times. Whatever is cut is in the tooltip.
        const minutes =
          (new Date(event.end_time).getTime() -
            new Date(event.start_time).getTime()) /
          60000;
        const roomy = minutes >= 45;
        const showTime = minutes >= 60;
        const titleLines = minutes >= 90 ? "line-clamp-3" : "line-clamp-2";

        return (
          <Tooltip
            key={event.id}
            content={`${event.summary} · ${timeRange}${isUnconfirmed ? ` · ${t("event.unconfirmed")}` : ""}`}
          >
            <div
              draggable={isDraggable}
              onDragStart={(e) => handleDragStart(e, event)}
              onDragEnd={onDragEnd}
              className={cn(
                // Tinted with the service's colour and edged with it, the
                // text in the foreground colour: white on the light service
                // colours was unreadable. The ring in the card's colour keeps
                // two overlapping blocks apart.
                "absolute overflow-hidden rounded-xs border-l-[3px] px-1.5 py-0.5 text-foreground ring-1 ring-card transition-[opacity,filter] hover:brightness-95 dark:hover:brightness-110",
                !event.is_primary && "opacity-70",
                isDraggable && "cursor-move",
                !isDraggable && "cursor-pointer",
                isBeingDragged && "opacity-50",
                // Not confirmed yet: a dashed outline, the slot may still free up.
                isUnconfirmed && "border border-l-[3px] border-dashed",
              )}
              style={{
                backgroundColor: `color-mix(in srgb, ${color} 24%, var(--card))`,
                borderColor: color,
                top: style.top,
                height: `calc(${style.height} - 1px)`,
                width: `calc( ${(eventsLayout.get(event.id)?.widthPercent || 1) * 100}% - 2px )`,
                left: `${(eventsLayout.get(event.id)?.leftPercent || 0) * 100}%`,
              }}
              onClick={(e) => {
                if (!isBeingDragged) {
                  e.stopPropagation();
                  onEventClick(event);
                }
              }}
            >
              {affectedAttendees && affectedAttendees.length > 0 && (
                <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-start gap-0.5 pl-0.5">
                  {affectedAttendees.map((attendee) => (
                    <div
                      key={attendee.email}
                      className="w-0.5 h-2 bg-muted-foreground/50 rounded-full"
                    />
                  ))}
                </div>
              )}
              <div
                className={cn(
                  "flex gap-1 text-xs font-semibold leading-tight",
                  roomy ? "items-start" : "items-center",
                )}
              >
                {/* Whether the appointment happens on site or over video changes how the
                    team prepares for it, so it has to be readable from the grid. */}
                {event.conference?.url && (
                  <VideoCameraIcon
                    weight="fill"
                    className="mt-px size-3 shrink-0"
                    aria-label={t("event.videoMeeting")}
                  />
                )}
                {isUnconfirmed && (
                  <ClockCounterClockwiseIcon
                    weight="fill"
                    className="mt-px size-3 shrink-0"
                    aria-label={t("event.unconfirmed")}
                  />
                )}
                {/* Wraps when the block is tall enough, instead of cutting
                    « Coupe femme — Amina Diallo » down to « Coupe femme — Amina… ». */}
                <span
                  className={cn(
                    "min-w-0",
                    roomy ? `${titleLines} break-words` : "truncate",
                  )}
                >
                  <TextWithUnsureName
                    text={event.summary}
                    unsureName={event.unsure_customer_name}
                    plain
                  />
                </span>
              </div>
              {showTime && (
                <div className="truncate text-[11px] leading-tight text-muted-foreground tabular-nums">
                  {isUnconfirmed ? t("event.unconfirmed") : timeRange}
                </div>
              )}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

const CurrentTime = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render every minute to update the current time indicator
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="absolute left-0 top-0 w-full h-1 pointer-events-none z-10"
      style={{
        top: `${((currentTime.getHours() * 60 + new Date().getMinutes()) / (24 * 60)) * 100}%`,
      }}
    >
      <div className="absolute left-0 top-0 w-2 h-2 bg-pink rounded-full -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute left-0 top-0 w-full h-0 border-t border-pink -translate-y-1/2" />
    </div>
  );
};
